import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Loader2, Send } from "lucide-react";
import { getProject, listProjects, listVoiceProfiles, uploadTakeFile } from "@/lib/studioApi";
import { getMix, saveMix } from "@/lib/mixDeskApi";
import { safeApiError } from "@/lib/safeErrorText";

const sel = "w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-[13px] text-white focus:border-rose-300/50 focus:outline-none";
const lbl = "mb-1 mt-3 block font-mono text-[10px] uppercase tracking-[0.16em] text-white/50";
/** The session's music is this version (or a Mix Desk mix of it) → the take lines up. */
const matches = (v, inst) => Boolean(inst && v.song_id && [inst.song_id, inst.source_song_id].includes(v.song_id));

/** Send to a song project → a new take on one of its versions, ready as "Your Voice" on that song's Mix Desk. */
export default function SendToProject({ makeFile, instrumental }) {
  const [projects, setProjects] = useState(null);
  const [pid, setPid] = useState("");
  const [project, setProject] = useState(null);
  const [vid, setVid] = useState("");
  const [profiles, setProfiles] = useState([]);
  const [singer, setSinger] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  useEffect(() => {
    listProjects({ limit: 60 }).then((rows) => {
      const ps = rows.filter((p) => p.versions_count);
      setProjects(ps);
      setPid(ps.find((p) => p.id === instrumental?.project_id)?.id || ps[0]?.id || "");
    }).catch(() => setProjects([]));
    listVoiceProfiles().then((ps) => { setProfiles(ps); setSinger(ps.find((p) => p.is_main)?.id || ps[0]?.id || ""); }).catch(() => {});
  }, [instrumental?.project_id]);
  useEffect(() => {
    if (!pid) return;
    setProject(null);
    setDone(null);
    getProject(pid).then((p) => {
      setProject(p);
      const vs = (p.versions || []).filter((v) => v.kind === "generated");
      setVid(vs.find((v) => matches(v, instrumental))?.id || vs[0]?.id || "");
    }).catch((e) => toast.error("Couldn't open that project", { description: safeApiError(e) }));
  }, [pid, instrumental]);
  const versions = useMemo(() => (project?.versions || []).filter((v) => v.kind === "generated"), [project]);
  const version = versions.find((v) => v.id === vid);
  const send = async () => {
    setBusy(true);
    try {
      const p = await uploadTakeFile(pid, { file: await makeFile(), versionId: vid, singerProfileId: singer || null });
      const take = (p.takes || []).filter((t) => t.version_id === vid).sort((a, b) => (a.created_at < b.created_at ? -1 : 1)).pop();
      let picked = false;
      if (take && version?.song_id) {
        try { const m = await getMix(version.song_id); await saveMix(version.song_id, { ...m.settings, take_id: take.id }); picked = true; } catch { /* Mix Desk not ready for this song yet */ }
      }
      setDone(version);
      toast.success(`Sent to “${project.title || "your song"}”`, { description: picked ? "It's now Your Voice on that song's Mix Desk." : "Pick it under Your Voice on that song's Mix Desk." });
    } catch (e) { toast.error("Couldn't send it", { description: safeApiError(e) }); }
    finally { setBusy(false); }
  };
  if (projects === null) return <Loader2 className="mt-4 h-5 w-5 animate-spin text-white/50" />;
  if (!projects.length) return <p className="mt-4 text-[13px] text-white/55">No song projects with a version yet — create a song first.</p>;
  return (
    <div data-testid="rec-send-project">
      <label className={lbl} htmlFor="rec-send-project-select">Song project</label>
      <select id="rec-send-project-select" data-testid="rec-send-project-select" value={pid} onChange={(e) => setPid(e.target.value)} className={sel}>
        {projects.map((p) => <option key={p.id} value={p.id}>{p.title || "Untitled song"}</option>)}
      </select>
      <label className={lbl} htmlFor="rec-send-version-select">Version</label>
      <select id="rec-send-version-select" data-testid="rec-send-version-select" value={vid} onChange={(e) => setVid(e.target.value)} disabled={!project} className={sel}>
        {versions.map((v, i) => <option key={v.id} value={v.id}>{v.title || `Version ${i + 1}`}{v.take ? ` · take ${v.take}` : ""}</option>)}
      </select>
      {project && !versions.length ? <p className="mt-2 text-[12px] text-amber-200">This project has no generated version to sing on yet.</p> : null}
      {profiles.length ? (
        <>
          <label className={lbl} htmlFor="rec-send-singer">Who is singing</label>
          <select id="rec-send-singer" data-testid="rec-send-singer" value={singer} onChange={(e) => setSinger(e.target.value)} className={sel}>
            {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </>
      ) : null}
      {version && !matches(version, instrumental) ? (
        <p data-testid="rec-send-align-warning" className="mt-3 flex gap-2 text-[12px] text-amber-200"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          This take was recorded over {instrumental ? `“${instrumental.name}”` : "no music"}. It lines up with this version only if that music matches it from the start.</p>
      ) : null}
      {done ? (
        <p data-testid="rec-send-done" className="mt-4 flex flex-wrap items-center gap-2 text-[13px] text-emerald-200"><CheckCircle2 className="h-4 w-4" /> Sent.
          {done.song_id ? <Link to={`/create/song/${done.song_id}/mix`} data-testid="rec-send-open-mix" className="underline">Open its Mix Desk</Link> : null}
          <Link to={`/create/project/${pid}`} className="underline">Open the project</Link></p>
      ) : null}
      <button type="button" data-testid="rec-send-project-btn" onClick={send} disabled={busy || !vid}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-rose-500 to-fuchsia-500 px-4 py-2.5 text-[13px] font-semibold disabled:opacity-40">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {busy ? "Sending…" : "Send to this project"}
      </button>
    </div>
  );
}
